import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type { CanvasProject, Persona } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { canvasKeys, listProjects } from '../api/canvas-captures';
import {
  CheckIcon,
  CloseIcon,
  LayersIcon,
  LockIcon,
  PeopleIcon,
  PlusIcon,
  SparkleIcon,
} from '../lib/icons';
import { PersonaAvatar } from './persona-avatar';
import { usePersonaPool } from './use-personas';

/**
 * "Meet your panel" — the same modal on first run and ever after.
 *
 * One component rather than an onboarding modal plus a manager, because they
 * show the same list with the same toggles; the only difference is the framing
 * around it and a step counter. Two components meant two places to add a
 * person, which is exactly where they drift.
 *
 * **A roster and a profile, not a grid of expanding cards.** Every person now
 * carries a working life, an income, motivations and a set of influences, and
 * putting that behind a disclosure inside a grid cell meant one card grew to
 * six times the height of its neighbours, shoved the row below it down the
 * page, and turned the modal into something you scrolled rather than read.
 * The list on the left never changes size, the profile on the right scrolls in
 * its own pane, and the modal is a **fixed height** — so reading about somebody
 * moves nothing, and there is exactly one scrollbar in play at a time.
 */

/** A labelled group of short facts, skipped entirely when there are none. */
function DetailList({ items, label }: { items: string[]; label: string }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-small-label text-text-muted uppercase">{label}</p>
      <ul className="mt-1.5 space-y-1">
        {items.map((item) => (
          <li
            className="relative pl-3.5 text-caption leading-relaxed text-text-secondary before:absolute before:top-[0.6em] before:left-0 before:size-1 before:rounded-full before:bg-text-muted"
            key={item}
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * One row of the roster.
 *
 * Two sibling buttons, never nested: the row selects, the check toggles
 * membership. Nesting them is invalid HTML and the inner one stops receiving
 * clicks — and the two actions genuinely differ, since reading about somebody
 * is not a reason to take them off the panel.
 */
function RosterRow({
  onSelect,
  onToggle,
  persona,
  selected,
}: {
  onSelect: () => void;
  onToggle: () => void;
  persona: Persona;
  selected: boolean;
}) {
  return (
    <div
      className={[
        'group relative flex items-center gap-2.5 rounded-card px-2.5 py-1.5 transition',
        selected ? 'bg-warm-alt ring-1 ring-hairline' : 'hover:bg-warm-alt/60',
      ].join(' ')}
    >
      {/* The selected marker is a bar rather than a fill: the row already
          carries the person's own accent, and two colours fighting over one
          row makes neither mean anything. */}
      <span
        aria-hidden="true"
        className={[
          'absolute top-2 bottom-2 -left-px w-[3px] rounded-full transition',
          selected ? 'bg-ink' : 'bg-transparent',
        ].join(' ')}
      />
      {/* Labelled explicitly: the row's own text would otherwise announce as
          "MI Maya Iqbal The Deliberate Value Seeker", and it sits beside a
          second button that also carries her name. */}
      <button
        aria-current={selected}
        aria-label={`Read about ${persona.name}`}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left"
        onClick={onSelect}
        type="button"
      >
        <PersonaAvatar
          className={persona.active ? '' : 'opacity-45'}
          persona={persona}
          size="row"
        />
        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-ui-strong leading-tight font-semibold ${persona.active ? 'text-text-primary' : 'text-text-muted'}`}
          >
            {persona.name}
          </span>
          {/* Truncated here *and* readable in full in the profile beside it,
              which is the trade the old card could not make. */}
          <span
            className="mt-0.5 block truncate text-tag-lg leading-snug text-text-muted"
            title={persona.title}
          >
            {persona.title}
          </span>
        </span>
      </button>
      <button
        aria-label={
          persona.active
            ? `Take ${persona.name} off the panel`
            : `Put ${persona.name} on the panel`
        }
        aria-pressed={persona.active}
        className={[
          'flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-sm transition',
          persona.active
            ? 'bg-ink text-highlight'
            : 'ring-1 ring-hairline-alt hover:ring-ink',
        ].join(' ')}
        onClick={onToggle}
        type="button"
      >
        {persona.active ? <CheckIcon className="size-3" /> : null}
      </button>
    </div>
  );
}

/** Everything the model wrote about one person, in a pane that scrolls on its
 *  own. Nothing here can change the size of anything else. */
function PersonaProfile({
  onRemoveFromProject,
  onToggle,
  persona,
}: {
  /** Takes them off this project's panel only. Absent outside a project. */
  onRemoveFromProject?: () => void;
  onToggle: () => void;
  persona: Persona;
}) {
  const demographics = persona.demographics;
  const professional = persona.professional;

  /** The one-line facts, joined only where they exist — a persona written
   *  before these fields existed has three of them, not seven. */
  const life = demographics
    ? [
        `${demographics.age}`,
        demographics.occupation,
        demographics.location,
        demographics.income,
        demographics.familyStatus,
        demographics.education,
      ].filter(Boolean)
    : [];

  return (
    // Keyed by the person in the parent, so switching cross-fades rather than
    // swapping text under the reader's eyes.
    <div className="duration-200 animate-in fade-in motion-reduce:animate-none">
      <div className="flex items-start gap-3.5">
        <PersonaAvatar persona={persona} size="xl" />
        <div className="min-w-0 flex-1">
          <h3 className="!my-0 !text-heading-sm !leading-tight !font-semibold !text-text-primary">
            {persona.name}
          </h3>
          <p className="mt-1 text-ui-strong leading-snug text-text-secondary">
            {persona.title}
          </p>
        </div>
        {/* Quiet when they are on the panel, solid when they are not: the
            filled state belongs to the action worth taking, and the only
            filled control on this screen should be Done. A black "On the
            panel" badge simply out-shouted it. */}
        <button
          className={[
            'flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-pill px-3 text-xs font-semibold transition',
            persona.active
              ? 'text-text-secondary ring-1 ring-hairline-alt hover:text-text-primary hover:ring-ink'
              : 'bg-ink text-white hover:bg-ink/90',
          ].join(' ')}
          onClick={onToggle}
          type="button"
        >
          {persona.active ? (
            <>
              <CheckIcon className="size-3" />
              On the panel
            </>
          ) : (
            'Add back to panel'
          )}
        </button>
      </div>

      {onRemoveFromProject ? (
        <button
          className="mt-2 cursor-pointer text-xs font-medium text-text-muted underline-offset-2 transition hover:text-text-primary hover:underline"
          onClick={onRemoveFromProject}
          type="button"
        >
          Remove from this panel
        </button>
      ) : null}

      {life.length > 0 ? (
        <p className="mt-4 border-t border-hairline pt-3.5 text-caption leading-relaxed text-text-muted">
          {life.join(' · ')}
        </p>
      ) : null}

      <p className="mt-2.5 text-ui-strong leading-relaxed text-text-primary">
        {persona.description}
      </p>

      {/* What makes them one of this product's people — the pet, the bill,
          the claim. First after the description because it is the first thing
          anyone in that market would ask about them. */}
      {persona.experiences?.length ? (
        <div className="mt-4">
          <DetailList items={persona.experiences} label="Their situation" />
        </div>
      ) : null}

      {/* Only when the product is read through a job. A consumer persona has
          none, and an invented job title would be noise on the card as much as
          it is in the prompt. */}
      {professional ? (
        <div className="mt-4 rounded-card bg-warm-alt p-3.5 ring-1 ring-hairline">
          <p className="text-ui font-semibold text-text-primary">
            {professional.jobTitle}
          </p>
          <p className="mt-0.5 text-xs text-text-muted">
            {[
              professional.industry,
              professional.experience,
              professional.companySize,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {professional.responsibilities.length > 0 ? (
            <p className="mt-2 text-caption leading-relaxed text-text-secondary">
              Answerable for {professional.responsibilities.join(', ')}.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Two columns so the four lists read as pairs — what they want beside
          why, what stops them beside what annoys them. */}
      <div className="mt-5 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <DetailList items={persona.goals} label="Goals" />
        <DetailList items={persona.motivations ?? []} label="Motivations" />
        <DetailList items={persona.painPoints} label="Pain points" />
        <DetailList items={persona.frustrations} label="Frustrations" />
        <DetailList items={persona.influencers ?? []} label="Listens to" />
      </div>

      <div className="mt-5 space-y-2 border-t border-hairline pt-4">
        <p className="text-caption leading-relaxed text-text-muted">
          <span className="text-small-label uppercase">Decides by</span>{' '}
          {persona.decisionMakingStyle}
        </p>
        <p className="text-caption text-text-muted">
          <span className="text-small-label uppercase">
            Comfort with software
          </span>{' '}
          {persona.techSavviness}
          {persona.brandLoyalty
            ? ` · sticks with what they use: ${persona.brandLoyalty}`
            : ''}
        </p>
      </div>
    </div>
  );
}

function ImportPersonas({
  bringing,
  onBring,
  onCancel,
  onPanel,
}: {
  bringing: boolean;
  onBring: (ids: string[]) => void;
  onCancel: () => void;
  /** Ids already on this panel, left out of the list. */
  onPanel: Set<string>;
}) {
  const { loading, pool } = usePersonaPool(true);
  const projectsQuery = useQuery({
    queryFn: listProjects,
    queryKey: canvasKeys.projects(),
  });
  const projectName = new Map(
    (projectsQuery.data ?? []).map((project) => [project.id, project.name]),
  );
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const candidates = pool.filter((persona) => !onPanel.has(persona.id));

  const fromLabel = (persona: Persona) => {
    const names = (persona.projectIds ?? [])
      .map((id) => projectName.get(id))
      .filter((name): name is string => Boolean(name));
    if (names.length) return `On ${names.join(', ')}`;
    const origin = persona.originProjectId
      ? projectName.get(persona.originProjectId)
      : undefined;
    return origin ? `From ${origin}` : 'Not on any panel';
  };

  return (
    <div className="duration-200 animate-in fade-in motion-reduce:animate-none">
      <h3 className="!my-0 !text-heading-xs !font-semibold !text-text-primary">
        Add from other rooms
      </h3>
      <p className="mt-1.5 text-ui leading-relaxed text-text-secondary">
        Bring people from your other rooms onto this panel. They stay in those
        rooms too.
      </p>

      {loading ? (
        <p className="mt-5 text-ui text-text-muted" role="status">
          Loading your people…
        </p>
      ) : candidates.length === 0 ? (
        <div className="mt-5 rounded-card border border-dashed border-hairline-alt px-4 py-7 text-center">
          <span className="mx-auto flex size-9 items-center justify-center rounded-pill bg-warm text-text-muted">
            <PeopleIcon className="size-4.5" />
          </span>
          <p className="mt-3 text-ui leading-snug font-medium text-text-primary">
            Everyone&apos;s already here
          </p>
          <p className="mt-1 text-xs leading-snug text-text-muted">
            People from your other rooms will show up here to add.
          </p>
        </div>
      ) : (
        <ul className="mt-4 space-y-1">
          {candidates.map((persona) => {
            const checked = picked.has(persona.id);
            return (
              <li key={persona.id}>
                <label className="flex cursor-pointer items-center gap-3 rounded-button px-2.5 py-2 transition hover:bg-warm-alt">
                  <input
                    checked={checked}
                    className="size-4 cursor-pointer accent-ink"
                    onChange={() => {
                      setPicked((current) => {
                        const next = new Set(current);
                        if (next.has(persona.id)) next.delete(persona.id);
                        else next.add(persona.id);
                        return next;
                      });
                    }}
                    type="checkbox"
                  />
                  <PersonaAvatar persona={persona} size="row" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-ui-strong font-medium text-text-primary">
                      {persona.name}
                    </span>
                    <span className="block truncate text-xs text-text-muted">
                      {persona.title} · {fromLabel(persona)}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          className="h-9 cursor-pointer rounded-button px-3 text-ui font-medium text-text-secondary transition hover:bg-surface-hover"
          onClick={onCancel}
          type="button"
        >
          {candidates.length === 0 && !loading ? 'Close' : 'Cancel'}
        </button>
        {(loading || candidates.length > 0) && (
          <button
            className="h-9 cursor-pointer rounded-button bg-ink px-4 text-ui font-semibold text-white transition hover:bg-ink/90 disabled:opacity-40"
            disabled={bringing || picked.size === 0}
            onClick={() => {
              onBring([...picked]);
            }}
            type="button"
          >
            {bringing
              ? 'Adding…'
              : picked.size > 1
                ? `Add ${String(picked.size)} to this panel`
                : 'Add to this panel'}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Step one of the introduction: what the product was read as.
 *
 * The panel is written from one screenshot and the project's generated description,
 * so this is not decoration — it is the input, shown before the output. A user
 * who disagrees with "a payments dashboard" now knows why the three people they
 * are about to meet are wrong, instead of concluding the feature is.
 */
function ProductStep({
  project,
  onContinue,
}: {
  project?: CanvasProject;
  onContinue: () => void;
}) {
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-small-label text-text-muted uppercase">
            From your first capture
          </p>
          <h2 className="!mt-2 !mb-0 !text-3xl !leading-tight !font-semibold !text-text-primary">
            Here&rsquo;s what we found
          </h2>
        </div>
        <span className="shrink-0 rounded-pill bg-tag-minor px-3 py-1.5 text-tag font-semibold tracking-[0.06em] text-text-secondary uppercase">
          Step 1 of 2
        </span>
      </div>

      <div className="mt-6 flex items-start gap-4 rounded-card bg-warm-alt p-5 ring-1 ring-hairline">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-button bg-highlight-soft text-ink">
          <SparkleIcon className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="text-base font-semibold text-text-primary">
            {project?.name ?? 'Your product'}
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-text-secondary">
            {project?.description ??
              'We read your first screen to work out what this product is for.'}
          </p>
        </div>
      </div>

      <p className="mt-5 text-sm leading-relaxed text-text-secondary">
        Your feedback panel is built from this — people who would plausibly use
        it.
      </p>

      <div className="mt-7 flex justify-end">
        <button
          className="h-12 cursor-pointer rounded-button bg-ink px-6 text-ui-lg font-semibold text-white transition hover:bg-ink/90 active:scale-[0.99]"
          onClick={onContinue}
          type="button"
        >
          Meet your panel →
        </button>
      </div>
    </>
  );
}

/** A heading inside the roster, pinned so the group a row belongs to is
 *  readable however far down the list you are. */
function RosterHeading({ children }: { children: string }) {
  return (
    <p className="text-small-label sticky top-0 z-10 bg-surface py-1.5 text-text-muted uppercase">
      {children}
    </p>
  );
}

/** How many people a room seats — the server's cap on a room, too. */
export const MAX_ON_PANEL = 5;

export function PanelModal({
  bringing = false,
  featureRequestFailed = false,
  featureRequested = false,
  project,
  onBring,
  onClose,
  onRemoveFromProject,
  onRequestFeature,
  onSuggestMore,
  onToggle,
  /** Set on first run: adds the product step in front, the step chips, and
   *  changes the closing button from "Done" to "Accept panel and continue". */
  onboarding = false,
  personas,
  requestingFeature = false,
  saveError = null,
  saving = false,
  suggesting = false,
}: {
  /** The product the panel was written from. Only used by the first step. */
  project?: CanvasProject;
  /** Brings account personas from other projects onto this panel. Absent
   *  outside a project, which hides "Add from other rooms". */
  onBring?: (ids: string[]) => void;
  bringing?: boolean;
  /** The last request did not go through. */
  featureRequestFailed?: boolean;
  /** Whether this person has already asked for custom personas. */
  featureRequested?: boolean;
  onClose: () => void;
  /** Takes somebody off this project's panel, never out of the account. */
  onRemoveFromProject?: (id: string) => void;
  /** Asks for custom personas. Absent hides the request footer. */
  onRequestFeature?: () => void;
  /** Absent when there is no capture in view to generate from. */
  onSuggestMore?: () => void;
  onToggle: (payload: { active: boolean; id: string }) => void;
  onboarding?: boolean;
  personas: Persona[];
  requestingFeature?: boolean;
  /** Why closing did not go through — the room could not be re-seated. */
  saveError?: string | null;
  /** The room is being re-seated with the panel as it now stands. */
  saving?: boolean;
  suggesting?: boolean;
}) {
  const [importOpen, setImportOpen] = useState(false);
  // The product step only exists on first run; every later visit is the
  // manager, which is one screen.
  const [step, setStep] = useState<1 | 2>(onboarding ? 1 : 2);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Which of the two panes a phone is showing. Ignored from `md` up, where
   *  both are on screen at once. */
  const [onPhoneShowing, setPhoneShowing] = useState<'profile' | 'roster'>(
    'roster',
  );

  const panel = personas;
  const selectedCount = panel.filter((persona) => persona.active).length;
  const full = selectedCount >= MAX_ON_PANEL;
  /** Somebody to seat, and nobody chosen — a room cannot be empty. */
  const nobody = panel.length > 0 && selectedCount === 0;
  // Taking somebody off always works; putting somebody on stops at the
  // cap, rather than seating them on the panel and leaving them out of the
  // room without a word.
  const toggle = (persona: Persona) => {
    if (!persona.active && full) return;
    onToggle({ active: !persona.active, id: persona.id });
  };
  // Falls back rather than storing the first id, so somebody being added,
  // retired or arriving from "suggest more" cannot leave the pane blank.
  const selected =
    panel.find((persona) => persona.id === selectedId) ?? panel[0];

  if (onboarding && step === 1) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/25 p-6 duration-200 animate-in fade-in">
        <div className="relative max-h-full w-full max-w-[640px] overflow-y-auto rounded-panel bg-surface p-8 shadow-floating duration-200 animate-in zoom-in-95 motion-reduce:animate-none">
          <ProductStep
            project={project}
            onContinue={() => {
              setStep(2);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/25 p-6 duration-200 animate-in fade-in">
      <button
        aria-label="Close"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        type="button"
      />

      {/* Fixed height, not max-height: the modal must not resize as people are
          selected, added or retired — that jump is most of what made the old
          one feel clunky. Everything that can grow scrolls inside a pane. */}
      <div className="relative flex h-[min(660px,88vh)] w-full max-w-[980px] flex-col overflow-hidden rounded-panel bg-surface shadow-floating duration-200 animate-in zoom-in-95 motion-reduce:animate-none">
        <header className="flex items-start justify-between gap-4 px-7 pt-6 pb-5">
          <div className="min-w-0">
            <p className="text-small-label text-text-muted uppercase">
              {onboarding ? 'Suggested from your product' : 'Your panel'}
            </p>
            <h2 className="!mt-1.5 !mb-0 !text-2xl !leading-tight !font-semibold !text-text-primary">
              {onboarding
                ? 'Meet your first feedback panel'
                : 'Meet your panel'}
            </h2>
            {/* One line, deliberately. Every pixel this header takes comes
                straight out of the roster beside it, and a three-line
                explanation left the last person sliced in half. */}
            <p className="mt-1.5 text-ui leading-relaxed text-text-secondary">
              Pick anyone to read their profile. The check takes them off.
            </p>
          </div>
          {onboarding ? (
            <span className="shrink-0 rounded-pill bg-tag-minor px-3 py-1.5 text-tag font-semibold tracking-[0.06em] text-text-secondary uppercase">
              Step 2 of 2
            </span>
          ) : (
            <button
              aria-label="Close"
              className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-button text-text-secondary transition hover:bg-surface-hover hover:text-text-primary"
              onClick={onClose}
              type="button"
            >
              <CloseIcon className="size-4" />
            </button>
          )}
        </header>

        {/* Two panes side by side on a laptop; on a phone, one at a time — the
            roster until somebody is picked, then their profile with a way
            back. Splitting the height instead gave a roster a couple of rows
            tall above a profile too short to read, which is neither. */}
        <div className="grid min-h-0 flex-1 border-t border-hairline md:grid-cols-[292px_minmax(0,1fr)]">
          <div
            className={[
              'min-h-0 flex-col border-hairline md:flex md:border-r',
              onPhoneShowing === 'roster' ? 'flex' : 'hidden',
            ].join(' ')}
          >
            <div className="relative min-h-0 flex-1">
              <div className="h-full overflow-y-auto px-4 py-3">
                <RosterHeading>People</RosterHeading>
                <div className="mt-1 space-y-0.5">
                  {panel.map((persona) => (
                    <RosterRow
                      key={persona.id}
                      onSelect={() => {
                        setSelectedId(persona.id);
                        setImportOpen(false);
                        setPhoneShowing('profile');
                      }}
                      onToggle={() => {
                        toggle(persona);
                      }}
                      persona={persona}
                      selected={!importOpen && selected?.id === persona.id}
                    />
                  ))}
                </div>
              </div>
              {/* Same cue as the profile pane: a roster whose last row is
                  sliced through reads as broken rather than as scrollable. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-7 bg-gradient-to-t from-surface to-transparent"
              />
            </div>

            <div className="flex flex-col gap-1 border-t border-hairline px-3 py-4">
              {onBring ? (
                <button
                  className="flex cursor-pointer items-center gap-2 rounded-button px-2.5 py-2.5 text-left text-ui leading-tight font-medium text-text-control transition hover:bg-warm-alt hover:text-text-primary"
                  onClick={() => {
                    trackEvent('persona_import_opened');
                    setImportOpen(true);
                    setPhoneShowing('profile');
                  }}
                  type="button"
                >
                  <LayersIcon className="size-3.5 shrink-0" />
                  Add from other rooms
                </button>
              ) : null}
              {/* The answer to importing a product the existing panel doesn't
                  really cover: more people into the *same* panel, never a
                  second one. Needs a screen to read, so it is absent where
                  there is no capture in view. */}
              {onSuggestMore ? (
                <button
                  className="flex cursor-pointer items-center gap-2 rounded-button px-2.5 py-2.5 text-left text-ui leading-tight font-medium text-text-control transition hover:bg-warm-alt hover:text-text-primary disabled:opacity-50"
                  disabled={suggesting}
                  onClick={onSuggestMore}
                  type="button"
                >
                  <SparkleIcon className="size-3.5 shrink-0" />
                  {suggesting
                    ? 'Writing them…'
                    : personas.length === 0
                      ? 'Suggest a panel from this product'
                      : 'Suggest more from this product'}
                </button>
              ) : null}

              {/* Writing your own isn't built yet: shown locked, with a way
                  to ask for it. Once asked, it says so rather than offering
                  again. */}
              {onRequestFeature ? (
                <div className="mt-2 border-t border-hairline px-2.5 pt-4 pb-1">
                  <div className="flex items-center gap-2 text-ui leading-tight font-medium text-text-control">
                    <PlusIcon className="size-3.5 shrink-0" />
                    <span className="flex-1">Create Custom Personas</span>
                    <LockIcon
                      aria-hidden={false}
                      aria-label="Not available yet"
                      role="img"
                      className="size-3.5 shrink-0"
                    />
                  </div>
                  <div className="mt-1.5 pl-5.5">
                    {featureRequested ? (
                      <p className="text-badge leading-snug text-text-control">
                        Feature requested! We&apos;ve let our team know!
                      </p>
                    ) : (
                      <button
                        className="cursor-pointer text-left text-caption leading-snug text-text-control underline underline-offset-2 transition hover:text-text-primary disabled:cursor-default disabled:opacity-50"
                        disabled={requestingFeature}
                        onClick={onRequestFeature}
                        type="button"
                      >
                        {requestingFeature
                          ? 'Requesting…'
                          : 'Want to create customized personas? Request Feature →'}
                      </button>
                    )}
                    {featureRequestFailed && !featureRequested ? (
                      <p className="mt-1 text-caption text-brand" role="alert">
                        Couldn&apos;t send that. Please try again.
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* The profile. The only pane that changes, and the only one allowed
              to be long. The fade at its foot is the only cue that there is
              more below — a pane that ends on a cleanly cut line of text reads
              as a rendering bug rather than as something to scroll. */}
          <div
            className={[
              'relative min-h-0 md:block',
              onPhoneShowing === 'profile' ? 'block' : 'hidden',
            ].join(' ')}
          >
            <div className="h-full overflow-y-auto px-7 py-6 max-md:px-5">
              {/* Phones only: the roster is not beside this pane, so there has
                  to be a way back to it. */}
              <button
                className="mb-4 flex cursor-pointer items-center gap-1.5 text-ui font-medium text-text-secondary transition hover:text-text-primary md:hidden"
                onClick={() => {
                  setPhoneShowing('roster');
                  setImportOpen(false);
                }}
                type="button"
              >
                ← Back to the panel
              </button>
              {importOpen && onBring ? (
                <ImportPersonas
                  bringing={bringing}
                  onBring={(ids) => {
                    onBring(ids);
                    setImportOpen(false);
                  }}
                  onCancel={() => {
                    setImportOpen(false);
                  }}
                  onPanel={new Set(panel.map((persona) => persona.id))}
                />
              ) : selected ? (
                <PersonaProfile
                  key={selected.id}
                  onRemoveFromProject={
                    onRemoveFromProject
                      ? () => {
                          onRemoveFromProject(selected.id);
                        }
                      : undefined
                  }
                  onToggle={() => {
                    toggle(selected);
                  }}
                  persona={selected}
                />
              ) : (
                <div>
                  <p className="text-ui-strong font-medium text-text-primary">
                    Nobody on this panel yet.
                  </p>
                  <p className="mt-1.5 text-ui leading-relaxed text-text-secondary">
                    Three people are written from this room&rsquo;s first
                    screen. You can also bring people over from your other
                    rooms.
                  </p>
                </div>
              )}
            </div>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-surface to-transparent"
            />
          </div>
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-4 border-t border-hairline px-7 py-4">
          <div>
            <p className="text-ui-strong font-semibold text-text-primary">
              {selectedCount} on the panel
            </p>
            {saveError ? (
              <p className="mt-0.5 text-xs text-brand" role="alert">
                {saveError}
              </p>
            ) : (
              <p className="mt-0.5 text-xs text-text-muted">
                {nobody
                  ? 'Pick at least one person to be in the room.'
                  : selectedCount > MAX_ON_PANEL
                    ? `Only the first ${String(MAX_ON_PANEL)} join the room.`
                    : full
                      ? `A room seats up to ${String(MAX_ON_PANEL)} people.`
                      : 'You can edit or add people later.'}
              </p>
            )}
          </div>
          <button
            className="h-11 cursor-pointer rounded-button bg-ink px-6 text-subtitle font-semibold text-white transition hover:bg-ink/90 active:scale-[0.99] disabled:cursor-default disabled:opacity-50"
            disabled={saving || nobody}
            onClick={onClose}
            type="button"
          >
            {saving
              ? 'Saving…'
              : onboarding
                ? 'Accept panel and continue →'
                : 'Done'}
          </button>
        </footer>
      </div>
    </div>
  );
}
